'use client'

import type { ChangeEvent } from 'react'
import type { FieldViewProps } from '../../types'
import type PercentController from './Controller'

import React, { useRef } from 'react'

import { Input } from '@/shared/components/ui/input'

import Combobox from '../../components/Combobox'
import ReferencePicker from '../../components/ReferencePicker'
import ReferenceWrapper from '../../components/ReferenceWrapper'
import { Operators } from '../../constants'
import { toOperationValueOption } from '../../utils/utils'
import { useFilterField } from '../useFilterField'

type PercentViewProps = FieldViewProps<PercentController>

const toPercent = (value: number | string) => {
	if (Number.isNaN(Number(value)) || value === '') {
		return value
	}

	return Number(value) * 100
}
const fromPercent = (value: number | string) => {
	if (Number.isNaN(Number(value)) || value === '') {
		return value
	}

	return Number(value) / 100
}
export default function PercentView({
	storedValue,
	field,
	invalidMessage,
	onChange,
	locale,
	disabled,
	fields,
	enableReferencing,
}: PercentViewProps) {
	const focusTimeoutId = useRef<any>(null)
	const operationsRef = useRef<HTMLButtonElement | null>(null)
	const inputRef = useRef<HTMLButtonElement | HTMLInputElement | null>(null)
	const filterField = useFilterField(field, {
		initialState: storedValue,
		onChange,
	})

	const focusValueInput = () => {
		focusTimeoutId.current = setTimeout(() => {
			inputRef.current?.focus?.()
		}, 10)
	}

	const handleChangeOperation = (operation: Operators) => {
		filterField.setFilterOperation(operation)
		focusValueInput()
	}
	const onRangeValueChange = (value: string, isMin?: boolean) => {
		const numValue = fromPercent(value) //Number(value)
		const fieldValue = filterField.getFilterValue()
		const currentValues = Array.isArray(fieldValue) ? fieldValue : ['', '']
		const otherValue = isMin
			? (currentValues[1] ?? '')
			: (currentValues[0] ?? '')
		if (value === '' || !Number.isNaN(numValue)) {
			filterField.setFilterValue(
				isMin ? [value, otherValue] : [otherValue, value]
			)
		}
	}
	const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
		filterField.setFilterValue(fromPercent(event.target.value) as string)
	}

	const operation = filterField.getFilterOperation()
	const value = filterField.getFilterValue()
	const mode = filterField.getFilterMode()

	const filterTypes = field.getFilterTypes().map((t) => ({
		...t,
		label: locale.getOperatorLabel(t.operation),
	}))
	const operators = filterTypes.map(toOperationValueOption)!
	const selectedTilterType = filterTypes.find((t) => t.operation === operation)!
	const isInvalid = Boolean(invalidMessage)
	const isBetween = (op: Operators, value: any): value is string[] =>
		op === Operators.BETWEEN

	return (
		<>
			<div className='flex items-center'>
				<Combobox
					locale={locale}
					data-testid='query-builder-operation-select'
					ref={operationsRef}
					name='operation'
					placeholder={locale.getOperationPlaceholder()}
					className='w-full'
					initialOpen={!operation}
					invalid={isInvalid}
					value={operation}
					onChange={handleChangeOperation}
					options={operators}
				/>
			</div>
			<div className='col-span-2 flex items-center'>
				{selectedTilterType?.hasInput ? (
					isBetween(operation, value) ? (
						<div className='flex w-full items-center gap-1'>
							<Input
								ref={inputRef as React.Ref<HTMLInputElement>}
								name='gt'
								data-testid='query-builder-value-number-gt'
								invalid={isInvalid}
								disabled={disabled}
								onChange={(e) => onRangeValueChange(e.target.value, true)}
								autoComplete='off'
								autoCorrect='off'
								type='number'
								value={toPercent(value[0])}
								className='w-full'
							/>
							<Input
								name='lt'
								data-testid='query-builder-value-number-lt'
								invalid={isInvalid}
								disabled={disabled}
								onChange={(e) => onRangeValueChange(e.target.value, false)}
								type='number'
								autoComplete='off'
								autoCorrect='off'
								value={toPercent(value[1])}
								className='w-full'
							/>
						</div>
					) : enableReferencing ? (
						<ReferenceWrapper filterField={filterField} locale={locale}>
							{mode === 'reference' ? (
								<ReferencePicker
									locale={locale}
									filterField={filterField}
									fields={fields}
									isInvalid={isInvalid}
								/>
							) : (
								<Input
									ref={inputRef as React.Ref<HTMLInputElement>}
									invalid={isInvalid}
									disabled={disabled}
									name='value'
									data-testid='query-builder-value-number-single'
									onChange={onChangeInput}
									type='number'
									autoComplete='off'
									autoCorrect='off'
									value={toPercent(value as string)}
									className='w-full'
									placeholder={locale.getEnterValuePlaceholder()}
								/>
							)}
						</ReferenceWrapper>
					) : (
						<Input
							ref={inputRef as React.Ref<HTMLInputElement>}
							invalid={isInvalid}
							disabled={disabled}
							name='value'
							data-testid='query-builder-value-number-single'
							onChange={onChangeInput}
							type='number'
							autoComplete='off'
							autoCorrect='off'
							value={toPercent(value as string)}
							className='w-full'
							placeholder={locale.getEnterValuePlaceholder()}
						/>
					)
				) : null}
			</div>
		</>
	)
}
